import ReactDatePicker from 'react-datepicker';
import { MouseEvent, ReactNode, useRef, useState } from 'react';
import noop from 'lodash/noop';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { FLOATING_MODIFIER } from './const';
import { DEFAULT_DATE_FORMAT } from '@/helpers/dateHelpers/dateHelpers';
import { DatePickerWrapperStyles, Wrapper } from './styles';
import { InputSizeVariant } from '../Input/styles';
import { Maybe } from '@/types/sharedTypes';
import { DatePickerHeader } from './ui/DatePickerHeader';
import { DatePickerInput } from './ui/DatePickerInput';

export type DateValueType = Maybe<Date>;

export type DataPickerType = {
  value: DateValueType;
  onChange?: (date: DateValueType) => void;
  label?: ReactNode;
  placeholder?: string;
  variant?: InputSizeVariant;
  isClearable?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  startDate?: Date;
  endDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  apiError?: string;
  error?: string;
  hasErrorComponent?: boolean;
  children?: ReactNode;
};

export const DatePicker = ({
  value,
  onChange = noop,
  label,
  placeholder,
  variant,
  isClearable,
  selectsStart,
  selectsEnd,
  startDate,
  endDate,
  minDate,
  maxDate,
  apiError,
  error,
  hasErrorComponent = true,
  children,
}: DataPickerType) => {
  const datePickerRef = useRef<ReactDatePicker>(null);
  const datePickerInputRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const onOpen = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOpen(!isOpen);
  };

  const onClose = () => setIsOpen(false);

  const onClear = () => onChange(null);

  const onSelect = (value: Date) => {
    onChange(value);
    setIsOpen(false);
  };

  useOutsideClick(wrapperRef, onClose);

  return (
    <Wrapper ref={wrapperRef}>
      <DatePickerWrapperStyles />
      <ReactDatePicker
        ref={datePickerRef}
        open={isOpen}
        calendarClassName='wv-datepicker'
        wrapperClassName='wv-datepicker'
        placeholderText={placeholder}
        selected={value}
        dateFormat={DEFAULT_DATE_FORMAT}
        showMonthDropdown
        showYearDropdown
        useWeekdaysShort
        onSelect={onSelect}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        popperModifiers={FLOATING_MODIFIER}
        popperPlacement='bottom-start'
        shouldCloseOnSelect
        showPopperArrow={false}
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        maxDate={maxDate}
        customInput={
          <DatePickerInput
            ref={datePickerInputRef}
            label={label}
            isClearable={isClearable}
            onOpen={onOpen}
            onClear={onClear}
            variant={variant}
            apiError={apiError}
            error={error}
            hasErrorComponent={hasErrorComponent}
          />
        }
        renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth }) => (
          <DatePickerHeader
            onYearSelect={changeYear}
            onMonthSelect={changeMonth}
            onDecreaseMonth={decreaseMonth}
            onIncreaseMonth={increaseMonth}
            date={date}
          />
        )}
      >
        {children && <div onClick={onClose}>{children}</div>}
      </ReactDatePicker>
    </Wrapper>
  );
};
